/*
  bvo-zoom-crop.js — stop the full-screen viewer painting twice.

  SYMPTOM (Morris, 2 Sept): "the 1st time clicking it jumps and gets bigger and then the
  2nd time its smaller. and then when i reload the page its bigger again."

  CAUSE, confirmed by instrumenting the live page: bvo-zoom.js crops each shot to the
  artwork's measured bounding box, but paints before that measurement has arrived. Its
  own comment calls the trade-off deliberate — "an asset still being measured … shows
  whole and centred rather than not at all" — so the first open renders the whole canvas
  (large), then re-lays out to the crop (smaller). The second open finds the box in
  BVO.optical's cache and lands correctly; a reload empties that cache and it happens
  again. Exactly the big-then-small-then-big-again sequence described.

  FIX: keep the panel invisible for the few milliseconds until its crop is known, then
  reveal it already correct. open() pre-measures every view, so the box is normally
  in flight before this runs and the wait is imperceptible.

  Deliberately a wrapper rather than an edit to bvo-zoom.js: that file owns the crop,
  pan and magnify logic and should stay the upstream viewer; this owns one timing
  decision. It hides the crop window itself rather than leaning on the viewer's
  is-loading class, so it does not depend on what that class happens to style.

  Safety: a 450ms timeout always reveals the panel, and any thrown error, missing
  element or absent BVO.optical leaves the viewer exactly as it was.
*/
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    var root = document.querySelector('[data-zoom-root]');
    if (!root) return;
    var img = root.querySelector('[data-zoom-image]');
    var optical = window.BVO && window.BVO.optical;
    if (!img || !optical || !optical.measure) return;

    var timer = null;
    var token = null;

    /* bvo-zoom.js builds the crop window at DOMContentLoaded, which may be after this
       runs, so it is looked up each time rather than captured once. */
    function frame() { return root.querySelector('[data-zoom-frame]'); }

    function reveal() {
      var f = frame();
      if (f) f.style.visibility = '';
      if (timer) { clearTimeout(timer); timer = null; }
    }

    function hold() {
      var src = img.currentSrc || img.src;
      if (!src) return;
      var f = frame();
      if (!f) return;

      token = src;
      f.style.visibility = 'hidden';
      if (timer) clearTimeout(timer);
      timer = setTimeout(reveal, 450);            // never leave the viewer empty

      optical.measure(src).then(function () {
        if ((img.currentSrc || img.src) !== token) return;
        // the box is cached now; let the viewer's own re-layout run, then show it
        window.requestAnimationFrame(function () { window.requestAnimationFrame(reveal); });
      })['catch'](reveal);
    }

    /* Capture phase, registered before the viewer's own load handler, so the panel is
       hidden before that handler lays it out at the uncropped size. */
    img.addEventListener('load', hold, true);

    /* Opening re-assigns src; when the file is already cached no load event follows,
       so the open itself is also a trigger. */
    new MutationObserver(function () {
      if (root.hidden) reveal(); else hold();
    }).observe(root, { attributes: true, attributeFilter: ['hidden'] });
  });
})();
